import React, { useRef } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import { Box, Card, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  MyCafeteriSubaccountsTable,
  MyCafeteriaInfoLine,
  MyCafeteriaStatementTitle,
  MyCafeteriaSzepApplicationPrint,
} from "my-cafeteria/components";
import { useMyCafeteriaCurrentStatementItems } from "my-cafeteria/hooks";
import { useMyHrEmployee } from "my-hr/hooks";
import { OcPageTransition, OcPrintHeader } from "oc/components";
import { useOcCurrentUserState } from "oc/hooks";
import { useOcIsMobile } from "oc/hooks/use-is-mobile";
import { getFullname } from "oc/utils";

export const MyCafeteriaSzepCardApplicationPage = () => {
  const history = useHistory();
  const componentRef = useRef(null);
  const isMobile = useOcIsMobile();
  const { t } = useTranslation();
  const theme = useTheme();

  let thisYear = new Date().getFullYear().toString();

  const historyBack = () => {
    history.goBack();
  };

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
  });

  let { contractCode, employeeCode } = useOcCurrentUserState();

  const { data: employeeData } = useMyHrEmployee({ employeeId: employeeCode });
  const { data } = useMyCafeteriaCurrentStatementItems({
    contractCode,
  });

  type StatementItem = typeof data extends (infer U)[] ? U : any;

  const filteredData = data?.filter(
    (item: StatementItem) => item?.menuItem?.isSZEPCardItem,
  );

  const uniqFilteredData = filteredData?.reduce(
    (acc: StatementItem[], currentItem: StatementItem) => {
      const existingItemIndex = acc?.findIndex(
        (item) => item?.menuItem?.code === currentItem?.menuItem?.code,
      );

      if (existingItemIndex === -1) {
        acc.push(currentItem);
      } else if (currentItem?.szepCardApplicationAmount > 0) {
        acc[existingItemIndex] = currentItem;
      }

      return acc;
    },
    [],
  );

  let params = {
    applicationDate: new Date().toLocaleDateString(),
    employeeName: getFullname(employeeData),
    idNumber: employeeData?.taxNumber,
    contractCode: contractCode,
    currentYear: new Date().getFullYear(),
    data: uniqFilteredData || [],
  };

  const hasPreviousBenefit = params?.data?.length > 0;

  const currentYear = params?.currentYear;

  return (
    <OcPageTransition>
      <Box padding={isMobile ? "16px 0px" : "16px"}>
        <OcPrintHeader
          title="SZÉP kártya nyilatkozat"
          onBack={historyBack}
          onPrint={handlePrint}
          isMobile={isMobile}
        />
        <Box height="16px" />
        <Card
          id="my-cafeteria-summary-card"
          variant="outlined"
          style={{
            borderRadius: "4px",
            border: `1px solid ${theme.palette.gray30.main}`,
          }}
        >
          <Box
            display="flex"
            flexDirection="column"
            justifyContent="space-between"
            height="100%"
            padding="16px"
          >
            <Box id="upper-component" paddingBottom="48px">
              <MyCafeteriaStatementTitle
                label={t("MY_CAFETERIA_SZEP_STATEMENT", { currentYear })}
              />
              <Box
                display="flex"
                flexDirection="row"
                justifyContent="space-between"
                paddingBottom="16px"
              >
                <Box id="data-block" width={isMobile ? "100%" : "auto"}>
                  <MyCafeteriaInfoLine
                    label={t("OC_CALENDAR_DATE")}
                    value={params?.applicationDate}
                    isMobile={isMobile}
                  />
                  <MyCafeteriaInfoLine
                    label={t("OC_EMPLOYEE_NAME")}
                    value={params?.employeeName ?? ""}
                    isMobile={isMobile}
                  />
                  <MyCafeteriaInfoLine
                    label={t("MY_CAFETERIA_TAX_ID_SIGN_ID")}
                    value={params?.idNumber}
                    isMobile={isMobile}
                  />
                  <MyCafeteriaInfoLine
                    label={t("MY_CAFETERIA_STATEMENT_CONTRACT_CODE")}
                    value={contractCode}
                    isMobile={isMobile}
                  />
                </Box>
              </Box>
              <Box padding={isMobile ? "0px" : "0px 56px"}>
                {hasPreviousBenefit ? (
                  <Box>
                    <Box paddingBottom="36px">
                      <Typography variant="body1">
                        {t("MY_CAFETERIA_SZEP_BENEFIT_NAME", { thisYear })}
                      </Typography>
                    </Box>
                    <Box
                      width={isMobile ? "100%" : "544px"}
                      display="flex"
                      flexDirection="column"
                    >
                      <Box display="flex" flexDirection="row">
                        <Box
                          flexGrow={1}
                          borderBottom={`1px solid ${theme.palette.gray80.main}`}
                          paddingBottom="10px"
                        >
                          <Typography variant="h6">
                            {t("MY_CAFETERIA_SUB_ACCOUNTS")}
                          </Typography>
                        </Box>
                        <Box
                          width="85px"
                          textAlign="right"
                          borderBottom={`1px solid ${theme.palette.gray80.main}`}
                          paddingBottom="10px"
                        >
                          <Typography variant="h6">{t("OC_AMOUNT")}</Typography>
                        </Box>
                      </Box>
                      {uniqFilteredData?.map((item: StatementItem) => {
                        return (
                          <MyCafeteriSubaccountsTable
                            key={item?._id}
                            label={item?.menuItem?.name}
                            value={item?.szepCardApplicationAmount}
                          />
                        );
                      })}
                    </Box>
                  </Box>
                ) : (
                  <Box>
                    <Typography variant="body1">
                      {t("MY_CAFETERIA_I_HAVE_NO_BENEFIT", { thisYear })}
                    </Typography>
                  </Box>
                )}
                <Box height="36px" />
                <Typography variant="body1">
                  {t("MY_CAFETERIA_SZEP_STAT_OF_RES")}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Card>
      </Box>
      <Box style={{ display: "none" }}>
        <MyCafeteriaSzepApplicationPrint
          ref={componentRef}
          applicationDate={params?.applicationDate}
          employeeName={params?.employeeName ?? ""}
          idNumber={params?.idNumber}
          contractCode={params?.contractCode}
          currentYear={params?.currentYear}
          hasPreviousBenefit={hasPreviousBenefit}
          thisYear={thisYear}
          data={uniqFilteredData}
        />
      </Box>
    </OcPageTransition>
  );
};

export default MyCafeteriaSzepCardApplicationPage;
